// Educational content and recommendations for each skin metric
// Used in the premium $99 analysis report

import type { MetricKey, MetricCategory, MetricInfo } from '@/types/analysis'

// Complete metric information for all 14 HD metrics
export const METRIC_INFO: Record<MetricKey, MetricInfo> = {
  pores: {
    key: 'pores',
    name: 'Pores',
    category: 'skinHealth',
    icon: '🔬',
    description: 'Visibility and size of skin pores',
    explanation:
      'Pores are small openings in your skin that release oils and sweat. Enlarged or visible pores are often caused by excess sebum production, aging, sun damage, or genetics. The T-zone (forehead, nose, chin) typically shows the most visible pores due to higher oil gland concentration.',
    ingredients: [
      'Niacinamide - Minimizes pore appearance and regulates sebum',
      'Salicylic Acid (BHA) - Clears pore congestion from within',
      'Retinol - Increases cell turnover and refines pore size',
      'Witch Hazel - Natural astringent to tighten pores',
    ],
    hasZones: true,
    zoneLabels: {
      forehead: 'Forehead',
      nose: 'Nose',
      cheeks: 'Cheeks',
    },
  },
  wrinkles: {
    key: 'wrinkles',
    name: 'Wrinkles',
    category: 'agingSigns',
    icon: '〰️',
    description: 'Fine lines and deeper wrinkles',
    explanation:
      'Wrinkles form due to decreased collagen production, repetitive facial expressions, sun exposure, and natural aging. Different areas show different types: forehead lines from raising eyebrows, crow\'s feet from smiling, and nasolabial folds from facial movement. Early intervention can slow their progression.',
    ingredients: [
      'Retinol/Retinoids - Gold standard for anti-aging, boosts collagen',
      'Peptides - Signal skin to produce more collagen',
      'Vitamin C - Antioxidant that protects and brightens',
      'Hyaluronic Acid - Plumps skin and reduces line visibility',
    ],
    hasZones: true,
    zoneLabels: {
      forehead: 'Forehead Lines',
      crowsFeet: "Crow's Feet",
      glabella: 'Frown Lines (Glabella)',
      nasolabial: 'Nasolabial Folds',
      marionette: 'Marionette Lines',
      periocular: 'Around Eyes',
    },
  },
  texture: {
    key: 'texture',
    name: 'Texture',
    category: 'skinHealth',
    icon: '🪨',
    description: 'Skin surface smoothness and evenness',
    explanation:
      'Skin texture refers to the smoothness and evenness of your skin surface. Rough texture can be caused by dead skin cell buildup, dehydration, sun damage, or skin conditions. Regular exfoliation and proper hydration can significantly improve texture.',
    ingredients: [
      'AHA (Glycolic/Lactic Acid) - Exfoliates surface dead skin cells',
      'BHA (Salicylic Acid) - Clears pores and smooths skin',
      'Niacinamide - Improves skin barrier and texture',
      'Centella Asiatica - Soothes and promotes healing',
    ],
    hasZones: false,
  },
  acne: {
    key: 'acne',
    name: 'Acne',
    category: 'skinHealth',
    icon: '🔴',
    description: 'Active breakouts and blemishes',
    explanation:
      'Acne occurs when hair follicles become clogged with oil and dead skin cells, leading to whiteheads, blackheads, or pimples. Factors include hormones, diet, stress, and bacteria. K-beauty focuses on gentle, multi-step approaches to prevent and treat breakouts without over-drying.',
    ingredients: [
      'Salicylic Acid (BHA) - Penetrates pores to clear congestion',
      'Tea Tree Oil - Natural antibacterial properties',
      'Centella Asiatica - Calms inflammation and aids healing',
      'Propolis - Antibacterial and healing properties',
    ],
    hasZones: false,
  },
  redness: {
    key: 'redness',
    name: 'Redness',
    category: 'skinHealth',
    icon: '🌡️',
    description: 'Skin redness and irritation levels',
    explanation:
      'Skin redness can be caused by sensitivity, rosacea, broken capillaries, inflammation, or environmental irritants. K-beauty emphasizes calming, hydrating ingredients to soothe reactive skin and strengthen the skin barrier.',
    ingredients: [
      'Centella Asiatica (Cica) - Powerful soothing and healing',
      'Aloe Vera - Cooling and anti-inflammatory',
      'Green Tea - Antioxidant and calming',
      'Madecassoside - Concentrated cica for inflammation',
    ],
    hasZones: false,
  },
  darkCircles: {
    key: 'darkCircles',
    name: 'Dark Circles',
    category: 'eyeArea',
    icon: '👁️',
    description: 'Under-eye darkness and discoloration',
    explanation:
      'Dark circles under the eyes can be caused by genetics, lack of sleep, aging, allergies, or thin skin revealing blood vessels. The under-eye area has the thinnest skin on the face and requires gentle, targeted treatment.',
    ingredients: [
      'Vitamin C - Brightens and reduces pigmentation',
      'Caffeine - Constricts blood vessels, reduces puffiness',
      'Niacinamide - Improves circulation and brightness',
      'Vitamin K - Helps with blood vessel visibility',
    ],
    hasZones: false,
  },
  eyeBags: {
    key: 'eyeBags',
    name: 'Eye Bags',
    category: 'eyeArea',
    icon: '💧',
    description: 'Puffiness and swelling under eyes',
    explanation:
      'Eye bags are caused by fluid retention, aging (weakened muscles), allergies, or lifestyle factors like sleep and salt intake. Morning puffiness often improves throughout the day, but persistent bags may require targeted treatment.',
    ingredients: [
      'Caffeine - Stimulates circulation and reduces swelling',
      'Peptides - Strengthen under-eye skin structure',
      'Ginseng - Improves circulation and energizes skin',
      'Cooling agents - Temporarily reduces puffiness',
    ],
    hasZones: false,
  },
  spots: {
    key: 'spots',
    name: 'Spots',
    category: 'skinHealth',
    icon: '🟤',
    description: 'Dark spots and hyperpigmentation',
    explanation:
      'Dark spots (hyperpigmentation) result from excess melanin production triggered by sun exposure, hormonal changes, acne scarring, or inflammation. K-beauty excels at brightening treatments that fade spots while preventing new ones.',
    ingredients: [
      'Vitamin C - Inhibits melanin production',
      'Arbutin - Gentle brightening from bearberry',
      'Niacinamide - Prevents melanin transfer to skin surface',
      'Tranexamic Acid - Targets stubborn pigmentation',
    ],
    hasZones: false,
  },
  oiliness: {
    key: 'oiliness',
    name: 'Oiliness',
    category: 'skinHealth',
    icon: '✨',
    description: 'Sebum production and shine levels',
    explanation:
      'Oiliness is caused by overactive sebaceous glands producing excess sebum. The T-zone (forehead, nose, chin) is typically oilier than the U-zone (cheeks). Over-cleansing can actually increase oil production, so balance is key.',
    ingredients: [
      'Niacinamide - Regulates sebum production',
      'BHA (Salicylic Acid) - Keeps pores clear',
      'Green Tea - Controls oil and provides antioxidants',
      'Kaolin Clay - Absorbs excess oil without over-drying',
    ],
    hasZones: true,
    zoneLabels: {
      tZone: 'T-Zone',
      uZone: 'U-Zone (Cheeks)',
    },
  },
  moisture: {
    key: 'moisture',
    name: 'Moisture',
    category: 'agingSigns',
    icon: '💦',
    description: 'Skin hydration levels',
    explanation:
      'Skin moisture refers to water content in the skin. Dehydrated skin lacks water (different from dry skin which lacks oil). Proper hydration plumps skin, reduces fine lines, and maintains skin barrier function. K-beauty\'s layering approach excels at building moisture.',
    ingredients: [
      'Hyaluronic Acid - Holds 1000x its weight in water',
      'Glycerin - Humectant that draws moisture to skin',
      'Ceramides - Strengthen moisture barrier',
      'Snail Mucin - Deep hydration and repair',
    ],
    hasZones: false,
  },
  firmness: {
    key: 'firmness',
    name: 'Firmness',
    category: 'agingSigns',
    icon: '💪',
    description: 'Skin elasticity and tightness',
    explanation:
      'Skin firmness depends on collagen and elastin levels, which naturally decrease with age. Loss of firmness leads to sagging and loss of facial contours. Prevention and stimulating new collagen production are key strategies.',
    ingredients: [
      'Retinol - Stimulates collagen production',
      'Peptides - Signal cells to produce collagen',
      'Vitamin C - Essential for collagen synthesis',
      'Adenosine - Firms and smooths skin',
    ],
    hasZones: false,
  },
  radiance: {
    key: 'radiance',
    name: 'Radiance',
    category: 'agingSigns',
    icon: '🌟',
    description: 'Skin glow and luminosity',
    explanation:
      'Radiance refers to your skin\'s natural glow and luminosity. Dull skin is often caused by dead skin cell buildup, dehydration, poor circulation, or lifestyle factors. K-beauty\'s "glass skin" trend focuses on achieving a healthy, dewy glow.',
    ingredients: [
      'Vitamin C - Brightens and adds glow',
      'Niacinamide - Improves overall skin tone',
      'AHA - Removes dull surface cells',
      'Galactomyces/Ferments - Enhance skin luminosity',
    ],
    hasZones: false,
  },
  droopyEyelidUpper: {
    key: 'droopyEyelidUpper',
    name: 'Upper Eyelid',
    category: 'eyeArea',
    icon: '👁️‍🗨️',
    description: 'Upper eyelid droopiness',
    explanation:
      'Upper eyelid drooping (ptosis) occurs when the skin loses elasticity and begins to sag. This is a natural part of aging but can be accelerated by sun exposure and lifestyle factors. Targeted eye care and facial exercises may help.',
    ingredients: [
      'Peptides - Strengthen eyelid skin',
      'Caffeine - Tightens and firms skin',
      'Retinol (gentle) - Promotes collagen in delicate area',
      'Adenosine - Firms and smooths',
    ],
    hasZones: false,
  },
  droopyEyelidLower: {
    key: 'droopyEyelidLower',
    name: 'Lower Eyelid',
    category: 'eyeArea',
    icon: '👁️‍🗨️',
    description: 'Lower eyelid sagging',
    explanation:
      'Lower eyelid sagging results from loss of collagen and fat pad displacement with age. The delicate under-eye area requires gentle, targeted treatments to maintain firmness and reduce visible aging.',
    ingredients: [
      'Peptides - Support skin structure',
      'Vitamin C - Brightens and firms',
      'Caffeine - Reduces puffiness and tightens',
      'Ceramides - Strengthen skin barrier',
    ],
    hasZones: false,
  },
}

// Get all metrics by category
export function getMetricsByCategory(category: MetricCategory): MetricInfo[] {
  return Object.values(METRIC_INFO).filter((m) => m.category === category)
}

// Metric categories with display info
export const METRIC_CATEGORIES: Record<MetricCategory, { name: string; description: string }> = {
  skinHealth: {
    name: 'Skin Health',
    description: 'Core skin condition indicators',
  },
  agingSigns: {
    name: 'Aging Signs',
    description: 'Age-related skin changes',
  },
  eyeArea: {
    name: 'Eye Area',
    description: 'Delicate eye region analysis',
  },
}

// Order of metrics for display (grouped by category)
export const METRIC_DISPLAY_ORDER: MetricKey[] = [
  // Skin Health (6)
  'pores',
  'texture',
  'acne',
  'redness',
  'spots',
  'oiliness',
  // Aging Signs (4)
  'wrinkles',
  'firmness',
  'radiance',
  'moisture',
  // Eye Area (4)
  'darkCircles',
  'eyeBags',
  'droopyEyelidUpper',
  'droopyEyelidLower',
]

// Radar chart metrics (subset for overview visualization)
export const RADAR_CHART_METRICS: MetricKey[] = [
  'pores',
  'texture',
  'wrinkles',
  'redness',
  'moisture',
  'radiance',
]
